import * as Sentry from "@sentry/react";

/**
 * Reports an error by logging it to the console and optionally sending it to Sentry.
 *
 * This utility function provides a centralized way to handle error reporting throughout
 * the application, ensuring consistent logging and optional external error tracking.
 *
 * @param error - The error to report. Can be any type (Error object, string, etc.)
 * @param message - Optional custom message to include with the error log
 * @param sendToSentry - Whether to send the error to Sentry for tracking (default: true)
 *
 * @example
 * ```typescript
 * // Basic error reporting
 * reportError(new Error('Something went wrong'));
 *
 * // Error with custom message
 * reportError(error, 'Failed to fetch user data');
 *
 * // Error without Sentry reporting
 * reportError(error, 'Debug error', false);
 * ```
 */
export function reportError(error: unknown, message?: string, sendToSentry = true): void {
    if (message) {
        console.error(message, error);
    } else {
        console.error(error);
    }
    if (sendToSentry) {
        Sentry.captureException(error);
    }
}
